import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setOrganizationIdGetter } from '../services/api';
import { Organization } from '../types';
import { useAuth } from './AuthContext';
import { DomainUtils } from '../utils/domain';

interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  switchOrganization: (organizationId: number) => Promise<void>;
  createOrganization: (name: string, subdomain: string) => Promise<void>;
  updateOrganization: (orgId: number, data: Partial<Organization>) => Promise<void>;
  inviteUser: (email: string) => Promise<void>;
  setCurrentOrganization: (org: Organization) => void;
  checkSubdomainAvailability: (subdomain: string) => Promise<boolean>;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organization: null,
  loading: true,
  error: null,
  switchOrganization: async () => {},
  createOrganization: async () => {},
  updateOrganization: async () => {},
  inviteUser: async () => {},
  setCurrentOrganization: () => {},
  checkSubdomainAvailability: async () => false,
});

export const useOrganization = () => useContext(OrganizationContext);

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { user } = useAuth();

  // Initialize organization data from user data
  useEffect(() => {
    if (user && !hasFetched.current) {
      try {
        setLoading(true);
        setError(null);

        // Set current organization from user data
        const currentOrg = user.current_organization;
        if (currentOrg) {
          setOrganization(currentOrg);
          // Set up the organization ID getter immediately when we have the current organization
          setOrganizationIdGetter(() => currentOrg.id);
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Failed to load organization data';
        setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      } finally {
        setLoading(false);
        hasFetched.current = true;
      }
    } else if (!user) {
      setLoading(false);
      // Clear the organization ID getter when user is not available
      setOrganizationIdGetter(() => undefined);
    }
  }, [user]);

  // Update organization ID getter when organization changes
  useEffect(() => {
    if (organization) {
      setOrganizationIdGetter(() => organization.id);
    } else {
      setOrganizationIdGetter(() => undefined);
    }
  }, [organization]);

  // Sync organization state when subdomain changes
  const syncOrganizationFromSubdomain = useCallback(async () => {
    if (!user) return;
    const currentSubdomain = DomainUtils.getSubdomain();
    if (!currentSubdomain) return;

    // Only update if the subdomain doesn't match the current organization
    if (organization && organization.subdomain === currentSubdomain) return;

    try {
      const response = await api.get('/api/organizations');
      const orgs: Organization[] = response.data;
      const matchingOrg = orgs.find(org => org.subdomain === currentSubdomain);
      if (matchingOrg && (!organization || organization.id !== matchingOrg.id)) {
        setOrganization(matchingOrg);
      }
    } catch (err) {
      // Silently fail
    }
  }, [user, organization]);

  // Listen for URL changes (including subdomain changes)
  useEffect(() => {
    const handleLocationChange = () => {
      syncOrganizationFromSubdomain();
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);

    // Also listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      setTimeout(handleLocationChange, 0);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [syncOrganizationFromSubdomain]);

  // Redirect to organization subdomain if user is on base domain
  useEffect(() => {
    // Only run this check if we're not loading and user is authenticated
    if (loading || !user) {
      return;
    }

    // Check if we're on the base domain (no subdomain)
    const currentSubdomain = DomainUtils.getSubdomain();

    // If we're already on a subdomain, no need to redirect
    if (currentSubdomain) {
      return;
    }

    // Paths that should not trigger a redirect to subdomain
    const PUBLIC_PATHS = [
      '/login',
      '/register',
      '/verify-email',
      '/forgot-password',
      '/reset-password',
      '/create-organization',
      '/invite',
    ];

    // Check if we're on a public path that shouldn't redirect
    const currentPath = window.location.pathname;
    const isPublicPath = PUBLIC_PATHS.some(path => currentPath.startsWith(path));

    if (isPublicPath) {
      return;
    }

    // If user is logged in and has a current organization, redirect to that subdomain
    if (user.current_organization) {
      const orgSubdomain = user.current_organization.subdomain;
      const orgUrl = DomainUtils.getOrganizationUrl(orgSubdomain);

      if (orgUrl) {
        // For root path, redirect to /forecast, otherwise preserve the current path
        const targetPath = currentPath === '/' ? '/forecast' : currentPath;
        const search = window.location.search || '';
        const hash = window.location.hash || '';
        const targetUrl = `${orgUrl}${targetPath}${search}${hash}`;

        window.location.href = targetUrl;
      }
    }
  }, [user, loading]);

  const switchOrganization = async (organizationId: number) => {
    try {
      const response = await api.post(`/api/organizations/${organizationId}/switch`);
      setOrganization(response.data);
      // Redirect to the new organization's forecast
      navigate(`/forecast`);
    } catch (error) {
      throw error;
    }
  };

  const createOrganization = async (name: string, subdomain: string) => {
    try {
      const response = await api.post('/api/organizations', { name, subdomain });
      setOrganization(response.data);
    } catch (error) {
      throw error;
    }
  };

  const updateOrganization = async (orgId: number, data: Partial<Organization>) => {
    try {
      const response = await api.patch(`/api/organizations/${orgId}`, data);
      if (organization?.id === orgId) {
        setOrganization(response.data);
      }
    } catch (error) {
      throw error;
    }
  };

  const inviteUser = async (email: string) => {
    try {
      await api.post(`/api/organizations/${organization?.subdomain}/invites`, { email });
    } catch (error) {
      throw error;
    }
  };

  const setCurrentOrganization = (org: Organization) => {
    setOrganization(org);
  };

  const checkSubdomainAvailability = async (subdomain: string) => {
    try {
      const response = await api.get(`/api/organizations/check-subdomain/${subdomain}`);
      return response.data.available;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return true; // Subdomain is available if endpoint returns 404
      }
      throw error;
    }
  };

  if (error) {
    return (
      <div className='error-message'>
        <h3>Error Loading Organization</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        loading,
        error,
        switchOrganization,
        createOrganization,
        updateOrganization,
        inviteUser,
        setCurrentOrganization,
        checkSubdomainAvailability,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
